/**
 * Theme accent — user-picked primary accent color that drives coral-related
 * gradients across the app. Stored in localStorage per-device.
 *
 * The default set matches v9999 tokens; adding a new accent is a matter of
 * extending PRESETS + updating the CSS variable emitter.
 */

export type AccentKey = 'coral' | 'mint' | 'lavender' | 'sky' | 'amber'

interface AccentPreset {
  key: AccentKey
  label: string
  hex: string
  hexDeep: string
  gradient: string          // linear-gradient CSS
  glow: string              // rgba shadow
}

export const ACCENT_PRESETS: AccentPreset[] = [
  { key: 'coral',    label: 'Coral',    hex: '#FF7A2D', hexDeep: '#E5601A', gradient: 'linear-gradient(180deg, #FF8942 0%, #FF7A2D 55%, #F26A1F 100%)', glow: 'rgba(255,122,45,0.55)' },
  { key: 'mint',     label: 'Mint',     hex: '#2ED2A3', hexDeep: '#1FA47F', gradient: 'linear-gradient(180deg, #40DDAF 0%, #2ED2A3 55%, #22B98D 100%)', glow: 'rgba(46,210,163,0.55)' },
  { key: 'lavender', label: 'Lavender', hex: '#9B7BB8', hexDeep: '#7B5C97', gradient: 'linear-gradient(180deg, #B497D3 0%, #9B7BB8 55%, #7B5C97 100%)', glow: 'rgba(155,123,184,0.55)' },
  { key: 'sky',      label: 'Sky',      hex: '#5BA3E0', hexDeep: '#3B7CB8', gradient: 'linear-gradient(180deg, #7DB8ED 0%, #5BA3E0 55%, #3B7CB8 100%)', glow: 'rgba(91,163,224,0.55)' },
  { key: 'amber',    label: 'Amber',    hex: '#FFB84D', hexDeep: '#E19A2F', gradient: 'linear-gradient(180deg, #FFC470 0%, #FFB84D 55%, #E19A2F 100%)', glow: 'rgba(255,184,77,0.55)' },
]

const LS_KEY = 'babyos.accent'

export function currentAccent(): AccentKey {
  if (typeof localStorage === 'undefined') return 'coral'
  const raw = localStorage.getItem(LS_KEY)
  if (raw && ACCENT_PRESETS.some((a) => a.key === raw)) return raw as AccentKey
  return 'coral'
}

export function setAccent(key: AccentKey): void {
  if (typeof localStorage !== 'undefined') localStorage.setItem(LS_KEY, key)
  applyAccentToDom(key)
}

/**
 * Emit CSS variables on :root so accent-driven gradients + shadows re-color live
 * without a rebuild. Called once on app boot + every time the user picks a new accent.
 */
export function applyAccentToDom(key: AccentKey = currentAccent()): void {
  if (typeof document === 'undefined') return
  const preset = ACCENT_PRESETS.find((a) => a.key === key) ?? ACCENT_PRESETS[0]
  const root = document.documentElement
  root.style.setProperty('--accent-primary', preset.hex)
  root.style.setProperty('--accent-primary-deep', preset.hexDeep)
  root.style.setProperty('--accent-primary-gradient', preset.gradient)
  root.style.setProperty('--accent-primary-glow', preset.glow)
  root.setAttribute('data-accent', key)
}
